import { createContext, useContext, type ReactNode, createElement } from 'react';
import { type ProductFrameId } from '@/lib/productFrames';

export interface VideoConfig {
  fps: number;
  durationInFrames: number;
  width: number;
  height: number;
}

export interface RemotionContextValue extends VideoConfig {
  frame: number;
}

export const RemotionCompositionContext = createContext<RemotionContextValue>({
  frame: 0,
  fps: 30,
  durationInFrames: 720,
  width: 1440,
  height: 900,
});

export function useCurrentFrame(): number {
  return useContext(RemotionCompositionContext).frame;
}

export function useVideoConfig(): VideoConfig {
  const { fps, durationInFrames, width, height } = useContext(RemotionCompositionContext);
  return { fps, durationInFrames, width, height };
}

export interface InterpolateOptions {
  extrapolateLeft?: 'clamp' | 'extend';
  extrapolateRight?: 'clamp' | 'extend';
}

export function interpolate(
  input: number,
  inputRange: readonly number[],
  outputRange: readonly number[],
  options: InterpolateOptions = { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' }
): number {
  if (inputRange.length < 2 || inputRange.length !== outputRange.length) {
    return outputRange[0] ?? 0;
  }
  if (input <= inputRange[0]) {
    if (options.extrapolateLeft === 'clamp') return outputRange[0];
  }
  const lastIdx = inputRange.length - 1;
  if (input >= inputRange[lastIdx]) {
    if (options.extrapolateRight === 'clamp') return outputRange[lastIdx];
  }

  let segIdx = 0;
  for (let i = 0; i < lastIdx; i++) {
    if (input >= inputRange[i] && input <= inputRange[i + 1]) {
      segIdx = i;
      break;
    }
    if (input > inputRange[i + 1]) {
      segIdx = i;
    }
  }

  const inMin = inputRange[segIdx];
  const inMax = inputRange[segIdx + 1];
  const outMin = outputRange[segIdx];
  const outMax = outputRange[segIdx + 1];
  if (inMax === inMin) return outMax;
  const ratio = (input - inMin) / (inMax - inMin);
  return outMin + ratio * (outMax - outMin);
}

export interface SpringArgs {
  frame: number;
  fps: number;
  config?: {
    damping?: number;
    stiffness?: number;
    mass?: number;
  };
}

export function spring({ frame, fps, config }: SpringArgs): number {
  if (frame <= 0) return 0;
  const damping = config?.damping ?? 14;
  const stiffness = config?.stiffness ?? 120;
  const mass = config?.mass ?? 1;
  const t = frame / Math.max(1, fps);
  const omega0 = Math.sqrt(stiffness / mass);
  const zeta = damping / (2 * Math.sqrt(stiffness * mass));
  if (zeta >= 1) {
    return 1 - (1 + omega0 * t) * Math.exp(-omega0 * t);
  }
  const omegaD = omega0 * Math.sqrt(1 - zeta * zeta);
  const env = Math.exp(-zeta * omega0 * t);
  const value =
    1 - env * (Math.cos(omegaD * t) + ((zeta * omega0) / omegaD) * Math.sin(omegaD * t));
  return Math.max(0, Math.min(1.04, value));
}

export function Sequence({
  from,
  durationInFrames,
  children,
}: {
  from: number;
  durationInFrames: number;
  children: ReactNode;
}) {
  const parent = useContext(RemotionCompositionContext);
  const localFrame = parent.frame - from;
  if (localFrame < 0 || localFrame >= durationInFrames) return null;
  return createElement(
    RemotionCompositionContext.Provider,
    {
      value: {
        ...parent,
        frame: localFrame,
        durationInFrames,
      },
    },
    children
  );
}

export function AbsoluteFill({
  children,
  className = '',
}: {
  children: ReactNode;
  className?: string;
}) {
  return createElement('div', { className: `absolute inset-0 ${className}` }, children);
}

export interface RecordlyZoomKeyframe {
  id: string;
  label: string;
  startFrame: number;
  endFrame: number;
  targetScale: number;
  focusX: number;
  focusY: number;
  description: string;
}

export interface RecordlyCursorWaypoint {
  frame: number;
  x: number;
  y: number;
  agentName: string;
  agentRole: string;
  actionLabel: string;
  click?: boolean;
}

export interface RecordlyChapter {
  id: string;
  frameId: ProductFrameId;
  secondaryStill: string;
  title: string;
  subtitle: string;
  agentId: string;
  startFrame: number;
  endFrame: number;
  metricBadge: string;
}

export const RECORDLY_CHAPTERS: RecordlyChapter[] = [
  {
    id: 'voice-intake',
    frameId: 'ai',
    secondaryStill: '/product-frames/ai-recordings.webp',
    title: 'AI Reception & Call Log',
    subtitle:
      'Summit Roofing Co. — Instant Quote Concierge & After-Hours Emergency Dispatch Line with recorded call transcripts',
    agentId: 'ashley',
    startFrame: 0,
    endFrame: 179,
    metricBadge: '100% Bookings',
  },
  {
    id: 'satellite-takeoff',
    frameId: 'estimates',
    secondaryStill: '/product-frames/roof-3d.webp',
    title: '3D Roof & Estimate',
    subtitle:
      'Measured roof estimate ($14,993.23 · 2,683 sq ft · 29.5 squares · 6/12 pitch) with 3D facet viewer and edge breakdown',
    agentId: 'tyler',
    startFrame: 180,
    endFrame: 359,
    metricBadge: '29.5 Squares · $14,993',
  },
  {
    id: 'leads-crm',
    frameId: 'dashboard',
    secondaryStill: '/product-frames/lead-detail.webp',
    title: 'Leads & Customer File',
    subtitle:
      'Marcus Whitfield (4821 Ridgemont Drive, Plano, TX 75024 · $24,850 · 96 Hot) from Lead Inbox to Send Contract',
    agentId: 'megan',
    startFrame: 360,
    endFrame: 539,
    metricBadge: '$208,600 Pipeline',
  },
  {
    id: 'milestone-escrow',
    frameId: 'pipeline',
    secondaryStill: '/product-frames/invoices.webp',
    title: 'Sales Pipeline & Invoices',
    subtitle:
      '4-stage Kanban board ($199k active) and Invoices ledger (IN-20841-1 Paid $13,450 · IN-20841-2 $11,400)',
    agentId: 'sofia',
    startFrame: 540,
    endFrame: 719,
    metricBadge: '$13,450 Paid',
  },
];

export const RECORDLY_ZOOM_KEYFRAMES: RecordlyZoomKeyframe[] = [
  {
    id: 'zoom-call-lock',
    label: '1.85x Call Lock',
    startFrame: 55,
    endFrame: 150,
    targetScale: 1.85,
    focusX: 62,
    focusY: 64,
    description: 'Zooming into Instant Quote Concierge call recording and transcript',
  },
  {
    id: 'zoom-3d-mesh',
    label: '2.05x 3D Mesh',
    startFrame: 225,
    endFrame: 330,
    targetScale: 2.05,
    focusX: 58,
    focusY: 48,
    description: 'Zooming into $14,993.23 Measured Roof Estimate and 3D Hip Roof model',
  },
  {
    id: 'zoom-hail-turf',
    label: '1.80x Lead File',
    startFrame: 405,
    endFrame: 510,
    targetScale: 1.8,
    focusX: 48,
    focusY: 36,
    description: 'Zooming into Marcus Whitfield 4821 Ridgemont Drive Send Contract action',
  },
  {
    id: 'zoom-escrow-draw',
    label: '1.90x Invoice Paid',
    startFrame: 585,
    endFrame: 690,
    targetScale: 1.9,
    focusX: 64,
    focusY: 48,
    description: 'Zooming into IN-20841-1 $13,450 Paid invoice row',
  },
];

export const RECORDLY_CURSOR_WAYPOINTS: RecordlyCursorWaypoint[] = [
  {
    frame: 0,
    x: 24,
    y: 24,
    agentName: 'Ashley',
    agentRole: 'AI Receptionist',
    actionLabel: 'Opening AI Agents workspace',
  },
  {
    frame: 48,
    x: 56,
    y: 28,
    agentName: 'Ashley',
    agentRole: 'AI Receptionist',
    actionLabel: 'Inspecting Instant Quote Concierge — Metroplex Storm Response Desk',
    click: true,
  },
  {
    frame: 95,
    x: 78,
    y: 62,
    agentName: 'Ashley',
    agentRole: 'AI Receptionist',
    actionLabel: 'Opening recorded call audio and transcript',
    click: true,
  },
  {
    frame: 180,
    x: 36,
    y: 22,
    agentName: 'Tyler',
    agentRole: '3D Estimator',
    actionLabel: 'Opening Measured Roof Estimate ($14,993.23)',
  },
  {
    frame: 235,
    x: 54,
    y: 42,
    agentName: 'Tyler',
    agentRole: '3D Estimator',
    actionLabel: 'Verifying 2,683 sq ft · 29.5 squares · 6/12 (26.6°)',
    click: true,
  },
  {
    frame: 295,
    x: 58,
    y: 52,
    agentName: 'Tyler',
    agentRole: '3D Estimator',
    actionLabel: 'Inspecting 3D Roof Viewer (4 facets · 1,342 sq ft)',
    click: true,
  },
  {
    frame: 360,
    x: 34,
    y: 62,
    agentName: 'Megan',
    agentRole: 'Speed-to-Lead',
    actionLabel: 'Selecting Marcus Whitfield ($24,850 · 96 Hot)',
  },
  {
    frame: 420,
    x: 34,
    y: 62,
    agentName: 'Megan',
    agentRole: 'Speed-to-Lead',
    actionLabel: 'Opening Marcus Whitfield Lead Detail (4821 Ridgemont Drive)',
    click: true,
  },
  {
    frame: 475,
    x: 27,
    y: 31,
    agentName: 'Megan',
    agentRole: 'Speed-to-Lead',
    actionLabel: 'Clicking Send Contract on 4821 Ridgemont Drive',
    click: true,
  },
  {
    frame: 540,
    x: 48,
    y: 58,
    agentName: 'Sofia',
    agentRole: 'Escrow & Billing',
    actionLabel: 'Reviewing Sales Pipeline Board ($199k Pipeline Value)',
  },
  {
    frame: 600,
    x: 72,
    y: 58,
    agentName: 'Sofia',
    agentRole: 'Escrow & Billing',
    actionLabel: 'Opening Invoices Ledger (IN-20841-1 · $13,450 Paid)',
    click: true,
  },
  {
    frame: 665,
    x: 93,
    y: 5,
    agentName: 'Sofia',
    agentRole: 'Escrow & Billing',
    actionLabel: 'Verified IN-20841-1 Paid / closed',
    click: true,
  },
  {
    frame: 719,
    x: 50,
    y: 50,
    agentName: 'Sofia',
    agentRole: 'Escrow & Billing',
    actionLabel: 'Walkthrough complete',
  },
];

export function computeRecordlyCamera(
  frame: number,
  fps: number,
  keyframes: readonly RecordlyZoomKeyframe[],
  autoZoomEnabled: boolean
) {
  if (!autoZoomEnabled) {
    return { scale: 1, panX: 0, panY: 0, activeKeyframe: null };
  }

  for (const kf of keyframes) {
    if (frame >= kf.startFrame && frame <= kf.endFrame) {
      const enterProgress = spring({
        frame: frame - kf.startFrame,
        fps,
        config: { damping: 16, stiffness: 110 },
      });
      const exitFramesLeft = kf.endFrame - frame;
      const exitProgress =
        exitFramesLeft < 24
          ? interpolate(exitFramesLeft, [0, 24], [0, 1], {
              extrapolateLeft: 'clamp',
              extrapolateRight: 'clamp',
            })
          : 1;
      const blend = Math.min(1, enterProgress) * exitProgress;
      const scale = 1 + (kf.targetScale - 1) * blend;
      const panX = (50 - kf.focusX) * 0.24 * blend;
      const panY = (50 - kf.focusY) * 0.2 * blend;
      return { scale, panX, panY, activeKeyframe: kf };
    }
  }

  return { scale: 1, panX: 0, panY: 0, activeKeyframe: null };
}

export function computeRecordlyCursor(
  frame: number,
  fps: number,
  waypoints: readonly RecordlyCursorWaypoint[]
) {
  if (waypoints.length === 0) {
    return {
      x: 50,
      y: 50,
      vx: 0,
      vy: 0,
      agentName: 'Ashley',
      agentRole: 'AI Receptionist',
      actionLabel: '',
      isClicking: false,
      clickScale: 1,
      rippleProgress: 0,
    };
  }

  let prev = waypoints[0];
  let next = waypoints[waypoints.length - 1];
  for (let i = 0; i < waypoints.length - 1; i++) {
    if (frame >= waypoints[i].frame && frame <= waypoints[i + 1].frame) {
      prev = waypoints[i];
      next = waypoints[i + 1];
      break;
    }
  }

  const span = Math.max(1, next.frame - prev.frame);
  const rawProgress = Math.max(0, Math.min(1, (frame - prev.frame) / span));
  const eased = spring({
    frame: rawProgress * 30,
    fps,
    config: { damping: 15, stiffness: 130 },
  });

  const x = prev.x + (next.x - prev.x) * Math.min(1, eased);
  const y = prev.y + (next.y - prev.y) * Math.min(1, eased);
  const vx = (next.x - prev.x) * (1 - Math.min(1, rawProgress));
  const vy = (next.y - prev.y) * (1 - Math.min(1, rawProgress));

  let isClicking = false;
  let rippleProgress = 0;
  for (const wp of waypoints) {
    if (wp.click && frame >= wp.frame && frame <= wp.frame + 22) {
      isClicking = true;
      rippleProgress = (frame - wp.frame) / 22;
      break;
    }
  }

  const clickScale = isClicking && rippleProgress < 0.35 ? 0.84 : 1;

  return {
    x,
    y,
    vx,
    vy,
    agentName: prev.agentName,
    agentRole: prev.agentRole,
    actionLabel: prev.actionLabel,
    isClicking,
    clickScale,
    rippleProgress,
  };
}
